import injectSheet from 'react-jss';
import * as React from 'react';
import { Modal, Table, Input } from 'antd';
import { useState, useEffect } from 'react';
import { StylesProps } from 'src/Library/Types';
import { RfqBidDetailsType } from '../../../SE.interfaces';

const Search: any = Input.Search;

const styles = (theme: any) => ({
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#424242',
  },
  table: {
    marginTop: '24px',
    borderRadius: '2px 2px 0 0',
    background: '#fff',
    border: '1px solid #E8E8E8',
    '& td': {
      verticalAlign: 'baseline',
    },
    '& .ant-table-thead': {
      fontSize: '14px',
      whiteSpace: 'pre-line !important',
      backgroundColor: '#f5f5f5',
      color: '#424242',
      padding: 0,
    },
    '& .ant-table-thead > tr > th, .ant-table-tbody > tr > td': {
      borderRight: '1px solid #E8E8E8',
    },
  },
  flexBox: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  search: {
    width: '20%',
  },
  textCol: {
    color: '#262626',
  },
  firstCol: {
    backgroundColor: '#F5F5F5',
    borderRight: '1px solid #E8E8E8',
    color: '#262626',
    fontWeight: 600,
  },
  flexContainer: {
    display: 'flex',
    flexDirection: 'column',
    marginLeft: '15px',
    marginRight: '15px',
  },
});

interface RfqBidDetailsModalProps extends StylesProps<ReturnType<typeof styles>> {
  onClose: () => void;
  visible: boolean;
  bidDetails: RfqBidDetailsType[];
}

const RfqBidDetailsModal: React.FunctionComponent<RfqBidDetailsModalProps> = ({
  visible,
  onClose,
  classes,
  bidDetails
}) => {
  let searchRef: any;
  const [searchText, setSearchText] = useState('');

  const filterBidDetails = (details: RfqBidDetailsType[], text: string) => {
    if (!text) {
      return details;
    }
    return details.filter((bidDetail: any) =>
      bidDetail.label?.toLowerCase().includes(text.toLowerCase()),
    );
  };

  const [dataSource, setDataSource] = useState(() => filterBidDetails(bidDetails, ''));

  // `bidDetails` can arrive/grow after mount — View Details opens immediately with the metadata rows and then
  // appends the full charge list once the async bid-details fetch resolves. `dataSource` is only seeded from
  // the prop at mount, so without this sync the modal would keep showing the partial list until it's closed
  // and reopened. Re-derive from the prop whenever it changes, but re-apply the active search term (if the
  // user typed one before the fetch resolved) instead of replacing the filtered view with the full list.
  useEffect(() => {
    setDataSource(filterBidDetails(bidDetails, searchText));
  }, [bidDetails, searchText]);

  const handleSearchTextChange = (text) => {
    setSearchText(text);
  }

  const columns = [
    {
      title: '',
      dataIndex: 'label',
      key: 'label',
      className: classes.firstCol,
      width: 200,
    },
    {
      title: '',
      dataIndex: 'value',
      key: 'value',
      render: (data) => {
        if (!data) {
          return '';
        }
        if (typeof data === 'object') {
          return `${data.currency} ${data.amount}`;
        }
        return data;
      }
    }
  ];

  const renderSearchBox = () => {
    return (
      <div className={classes.flexContainer}>
        <div className={classes.flexBox}>
          <Search
            className={classes.search}
            placeholder={'Search'}
            value={searchText}
            onChange={e => handleSearchTextChange(e.target.value)}
            ref={ref => searchRef = ref}
          />
        </div>
      </div>
    );
  }

  return (
    <Modal
      title={<div className={classes.title}>{'Detail View'}</div>}
      bodyStyle={{ paddingLeft: '0px', paddingRight: '0px', paddingTop: '24px' }}
      maskClosable={false}
      centered
      style={{ minWidth: '85%', marginTop: 0 }}
      onOk={() => { }}
      onCancel={() => onClose()}
      visible={visible}
      footer={null}
      destroyOnClose={true}>
      {renderSearchBox()}
      {<Table
        pagination={false}
        showHeader={false}
        columns={columns}
        className={classes.table}
        style={{marginTop: '24px'}}
        dataSource={dataSource &&
          dataSource.length ? dataSource : []}
      />}
    </Modal>
  );
}

export default injectSheet(styles)(RfqBidDetailsModal);
